import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { X, ChevronRight, ChevronLeft, Lightbulb, Target, Zap, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
const onboardingSteps = [
    {
        id: 'welcome',
        title: 'Welcome to SpeedPress!',
        description: 'Let\'s take a quick tour to help you get the most out of your WooCommerce optimization suite.',
        target: 'dashboard',
        position: 'bottom',
        icon: _jsx(Lightbulb, { className: "w-5 h-5 text-blue-500" })
    },
    {
        id: 'dashboard',
        title: 'Dashboard Overview',
        description: 'Monitor your store\'s performance metrics, active addons, and recent activities. Numbers animate each time you visit!',
        target: 'dashboard-stats',
        position: 'bottom',
        icon: _jsx(Target, { className: "w-5 h-5 text-purple-500" })
    },
    {
        id: 'addons',
        title: 'Browse Addon Categories',
        description: 'Explore 150+ addons organized by functionality. Click any category to see available addons.',
        target: 'sidebar-categories',
        position: 'right',
        icon: _jsx(Zap, { className: "w-5 h-5 text-green-500" }),
        action: {
            text: 'View Product Addons',
            callback: () => { }
        }
    },
    {
        id: 'search',
        title: 'Global Search',
        description: 'Quickly find any addon across all categories with real-time search filtering.',
        target: 'global-search',
        position: 'bottom',
        icon: _jsx(CheckCircle, { className: "w-5 h-5 text-orange-500" })
    },
    {
        id: 'configuration',
        title: 'Configure Addons',
        description: 'Each addon has detailed configuration options. Enable what you need and customize settings to fit your store.',
        target: 'addon-cards',
        position: 'top',
        icon: _jsx(Target, { className: "w-5 h-5 text-indigo-500" })
    }
];
export function OnboardingTour({ isVisible, onComplete, onSkip, onTabChange }) {
    const [currentStep, setCurrentStep] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);
    const currentStepData = onboardingSteps[currentStep];
    const isLastStep = currentStep === onboardingSteps.length - 1;
    const isFirstStep = currentStep === 0;
    useEffect(() => {
        if (!isVisible)
            return;
        // Handle step-specific actions
        if (currentStepData.id === 'addons' && onTabChange) {
            // Navigate to product-addons tab for demonstration
            setTimeout(() => {
                onTabChange('product-addons');
            }, 1000);
        }
    }, [currentStep, isVisible, onTabChange, currentStepData.id]);
    const handleNext = () => {
        if (isLastStep) {
            onComplete();
            return;
        }
        setIsAnimating(true);
        setTimeout(() => {
            setCurrentStep(prev => prev + 1);
            setIsAnimating(false);
        }, 200);
    };
    const handlePrevious = () => {
        if (isFirstStep)
            return;
        setIsAnimating(true);
        setTimeout(() => {
            setCurrentStep(prev => prev - 1);
            setIsAnimating(false);
        }, 200);
    };
    const handleStepClick = (stepIndex) => {
        if (stepIndex === currentStep)
            return;
        setIsAnimating(true);
        setTimeout(() => {
            setCurrentStep(stepIndex);
            setIsAnimating(false);
        }, 200);
    };
    const handleActionClick = () => {
        var _a;
        if ((_a = currentStepData.action) === null || _a === void 0 ? void 0 : _a.callback) {
            currentStepData.action.callback();
        }
        handleNext();
    };
    if (!isVisible)
        return null;
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: "fixed inset-0 bg-black/50 z-50" }), _jsx("div", { className: "fixed inset-0 z-[60] flex items-center justify-center p-4", children: _jsxs("div", { className: `bg-card border border-border rounded-xl shadow-2xl max-w-md w-full transform transition-all duration-200 ${isAnimating ? 'scale-95 opacity-80' : 'scale-100 opacity-100'}`, children: [_jsxs("div", { className: "flex items-center justify-between p-6 pb-4", children: [_jsxs("div", { className: "flex items-center gap-3", children: [currentStepData.icon, _jsxs("div", { children: [_jsx("h3", { className: "font-semibold", children: currentStepData.title }), _jsx("div", { className: "flex items-center gap-2 mt-1", children: _jsxs(Badge, { variant: "secondary", className: "text-xs", children: ["Step ", currentStep + 1, " of ", onboardingSteps.length] }) })] })] }), _jsx("button", { onClick: onSkip, className: "p-1 hover:bg-accent rounded-lg transition-colors", children: _jsx(X, { className: "w-4 h-4 text-muted-foreground" }) })] }), _jsx("div", { className: "px-6 pb-4", children: _jsx("div", { className: "flex gap-1", children: onboardingSteps.map((_, index) => (_jsx(Button, { onClick: () => handleStepClick(index), className: `flex-1 h-2 rounded-full transition-all duration-300 ${index <= currentStep
                                        ? 'bg-blue-500'
                                        : 'bg-muted'} ${index === currentStep ? 'ring-2 ring-blue-500/30' : ''}` }, index))) }) }), _jsxs("div", { className: "px-6 pb-6", children: [_jsx("p", { className: "text-muted-foreground leading-relaxed mb-6", children: currentStepData.description }), currentStepData.id === 'welcome' && (_jsxs("div", { className: "bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-950/20 dark:to-purple-950/20 rounded-lg p-4 mb-6", children: [_jsx("h4", { className: "font-medium mb-2 text-blue-800 dark:text-blue-200", children: "Quick Setup Tips:" }), _jsxs("ul", { className: "text-sm text-blue-700 dark:text-blue-300 space-y-1", children: [_jsx("li", { children: "\u2022 Start with free addons to test functionality" }), _jsx("li", { children: "\u2022 Enable performance monitoring first" }), _jsx("li", { children: "\u2022 Configure cart recovery for immediate ROI" })] })] })), currentStepData.id === 'dashboard' && (_jsxs("div", { className: "bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20 rounded-lg p-4 mb-6", children: [_jsx("h4", { className: "font-medium mb-2 text-green-800 dark:text-green-200", children: "Pro Tip:" }), _jsx("p", { className: "text-sm text-green-700 dark:text-green-300", children: "Click on any statistic card to see detailed breakdowns and trends over time." })] })), currentStepData.action && (_jsx("div", { className: "mb-4", children: _jsxs(Button, { onClick: handleActionClick, variant: "outline", size: "sm", className: "w-full", children: [currentStepData.action.text, _jsx(ChevronRight, { className: "w-4 h-4 ml-2" })] }) })), _jsxs("div", { className: "flex items-center justify-between", children: [_jsxs(Button, { onClick: handlePrevious, variant: "ghost", size: "sm", disabled: isFirstStep, className: "flex items-center gap-2", children: [_jsx(ChevronLeft, { className: "w-4 h-4" }), "Previous"] }), _jsx(Button, { onClick: onSkip, variant: "ghost", size: "sm", className: "text-muted-foreground hover:text-foreground", children: "Skip Tour" }), _jsxs(Button, { onClick: handleNext, size: "sm", className: "flex items-center gap-2", children: [isLastStep ? 'Complete' : 'Next', !isLastStep && _jsx(ChevronRight, { className: "w-4 h-4" }), isLastStep && _jsx(CheckCircle, { className: "w-4 h-4" })] })] })] })] }) })] }));
}
